import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, change, getFormValues, isDirty } from 'redux-form';
import { isEmpty } from 'lodash';
import { compose } from 'redux';
import { Form } from 'react-bootstrap';
import getLanguageSelectOptions from '../../../../../shared/utils/LanguageUtils';
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import BasicNumericInput from '../../../../../shared/components/formComponents/groups/BasicNumericInput';
import { VerbRichTextEditor } from '../../../../../shared/components/formComponents/VerbRichTextEditor';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import { SUPPORTED_LANGUAGES } from '../../../../../shared/Constants';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';

class CompanySettingsForm extends Component {
    static propTypes = {
        handleSubmit: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        onCancel: PropTypes.func,
        submitting: PropTypes.bool,
        languageOptions: PropTypes.array,
        error: PropTypes.string,
        initialValues: PropTypes.object,
        changeFormValue: PropTypes.func,
        currentValues: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func,
        company: {
            _id: PropTypes.string
        }
    };

    componentDidUpdate(prevProps) {
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    render() {
        const { submitting, handleSubmit, onCancel, error, languageOptions, company } = this.props;
        const isEdit = !!company._id;

        const normalizeCompanyPermalink = (value) => {
            let formattedValue = '';
            if (value) {
                formattedValue = value.toLocaleLowerCase();
                if (formattedValue[0] === '/') formattedValue = formattedValue.slice(1);
            }
            return formattedValue;
        };

        return (
            <Form className={'companyForm container'}>
                <Field
                    name={'name'}
                    label={'Company Name'}
                    placeholder={'My Company'}
                    className={'row company-form-field'}
                    component={BasicText}
                    description={'This is the name of the company as it will appear publicly on Verb.'}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    disabled={isEdit}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                <Field
                    name={'companyNames'}
                    label={'Company Login Names'}
                    placeholder={'Whole Foods, WF, Whole Food'}
                    className={'row company-form-field'}
                    component={BasicText}
                    normalize={(value) => value && value.toLocaleLowerCase()}
                    description={
                        'Company login names listed will be used to properly verify employee login. If more than one company login name, separate with commas.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                <Field
                    name={'permalink'}
                    label={'Company Permalink'}
                    placeholder={'companyName'}
                    className={'row company-form-field'}
                    component={BasicText}
                    normalize={normalizeCompanyPermalink}
                    description={'Slug used in the URL of your company page. [http://app.verb.net/../companyName]'}
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    disabled={isEdit}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                />
                <Field
                    name={'licenseNumber'}
                    label={'Licenses'}
                    placeholder={''}
                    minValue={0}
                    className={'row company-form-field'}
                    component={BasicNumericInput}
                    inputClassName={'col-5'}
                    description={'Sets how many employees can sign up.'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                />
                <Field
                    name={'isActive'}
                    className={'row company-form-field'}
                    inputClassName={'col-12 col-sm-6'}
                    label={'Active'}
                    component={BasicToggle}
                    description={'Sets whether this company is currently active.'}
                    companyForm={true}
                    styleProps={{ horizontal: true }}
                    id={'company-settings-is-active-toggle'}
                />
                <Field
                    name={'isCountedTowardsWAU'}
                    className={'row company-form-field'}
                    label={'Include In Reports'}
                    component={BasicToggle}
                    description={`Toggle off for companies that are for demo purposes only.`}
                    companyForm={true}
                    inputClassName={'col-12 col-sm-6'}
                    styleProps={{ horizontal: true }}
                    id={'company-settings-is-counted-towards-wau-toggle'}
                />
                <Field
                    name={'isICPCustomer'}
                    className={'row company-form-field'}
                    label={'ICP Customer'}
                    component={BasicToggle}
                    description={`Used only for reporting on company metrics`}
                    companyForm={true}
                    inputClassName={'col-12 col-sm-6'}
                    styleProps={{ horizontal: true }}
                    id={'company-settings-is-icp-customer-toggle'}
                />
                <Field
                    name={'lockUnlicensed'}
                    className={'row company-form-field'}
                    label={'Trial Edition'}
                    component={BasicToggle}
                    description={
                        'Employees only have access to licensed Collections. Other Collections will be locked.'
                    }
                    inputClassName={'col-12 col-sm-6'}
                    styleProps={{ horizontal: true }}
                    id={'company-settings-is-trial-toggle'}
                />
                <Field
                    name={'emailDomains'}
                    label={'Email Domains'}
                    placeholder={'example.com'}
                    className={'row company-form-field'}
                    component={BasicText}
                    normalize={(value) => value && value.toLocaleLowerCase()}
                    description={
                        'Domains allowed for employee email addresses during signup. Separate multiple domains with commas.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                    id={'company-settings-email-domains-toggle'}
                />
                <Field
                    name={'emailDomainsBlacklist'}
                    label={'Email Domains Not Allowed'}
                    placeholder={'example.com'}
                    className={'row company-form-field'}
                    component={BasicText}
                    normalize={(value) => value && value.toLocaleLowerCase()}
                    description={
                        'Domains not allowed during login. These can also be domains for users that should login via SSO. Separate multiple domains with commas.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true, 'full-width': true } }}
                    id={'company-settings-blacklist-toggle'}
                />

                <Field
                    name={'language'}
                    label={'Preferred Language'}
                    className={'row company-form-field'}
                    options={languageOptions}
                    component={BasicSelect}
                    description={
                        'This is the default language used across the Verb platform by members of this company.'
                    }
                    inputClassName={'col-12 col-sm-5 no-padding'}
                    styleProps={{ horizontal: true, inputStyle: { bordered: true } }}
                    id={'company-settings-preferred-language-toggle'}
                />
                <Field
                    name={'hasTOSWall'}
                    className={'row company-form-field'}
                    inputClassName={'col-12 col-sm-6'}
                    label={'Terms of Service'}
                    component={BasicToggle}
                    description={
                        'Blocks users from using Verb unless they agree with legal wording upon first time sign in.'
                    }
                    styleProps={{ horizontal: true }}
                    id={'company-settings-TOS-toggle'}
                />
                <Field
                    component={VerbRichTextEditor}
                    name={'TOSTerms'}
                    label={'Terms of Service Text'}
                    className={'col-12 col-sm-11 company-form-field'}
                    disableLinkButton={true}
                    inputClassName={'col-12 col-sm-10 no-padding'}
                    styleProps={{ horizontal: true }}
                />
                <br />
                <div className={'col-12 formButtons'}>
                    <VerbButton
                        fontAwesomeIcon={'fa-save'}
                        textId={'BUTTON_SAVE'}
                        defaultMessage={'SAVE'}
                        onClick={(values) => {
                            this.setState({ formSubmitted: true });
                            handleSubmit(values);
                        }}
                        disabled={submitting}
                    />
                    <VerbButton
                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                        fontAwesomeIcon={'fa-ban'}
                        textId={'BUTTON_CANCEL'}
                        defaultMessage={'CANCEL'}
                        onClick={onCancel}
                        disabled={submitting}
                    />
                </div>
                <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
            </Form>
        );
    }
}

export const CompanySettingsFormComponent = CompanySettingsForm;
export const makeMapStateToProps = () => {
    const mapStateToProps = (state, ownProps) => {
        const { isNew } = ownProps;

        const {
            name,
            licenseNumber,
            trialEndDate,
            permalink,
            lockUnlicensed,
            isActive = true,
            language = SUPPORTED_LANGUAGES.en.localeCode,
            hasTOSWall,
            TOSTerms,
            emailDomains,
            emailDomainsBlacklist,
            companyNames,
            isCountedTowardsWAU,
            isICPCustomer,
            customHome
        } = ownProps.company;

        return {
            currentValues: getFormValues('CompanySettings')(state),
            dirty: isDirty('CompanySettings'),
            initialValues: {
                name,
                trialEndDate,
                licenseNumber,
                permalink,
                lockUnlicensed,
                isActive,
                language,
                hasTOSWall,
                TOSTerms,
                emailDomains: emailDomains && emailDomains.join(', '),
                emailDomainsBlacklist: emailDomainsBlacklist && emailDomainsBlacklist.join(', '),
                companyNames: companyNames && companyNames.join(', '),
                isCountedTowardsWAU: isNew ? true : isCountedTowardsWAU,
                isICPCustomer: isNew ? false : isICPCustomer,
                customHome
            },
            languageOptions: getLanguageSelectOptions()
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    changeFormValue: (params) => dispatch(change('CompanySettings', params.field, params.value))
});

const CompanySettingsFormValidation = {
    form: 'CompanySettings',
    destroyOnUnmount: false,
    enableReinitialize: true,
    validate: (values) => {
        const errors = {};
        if (!isEmpty(values.name) && values.name.length > 250) {
            errors.name = 'Name is too long.';
        }
        if (isEmpty(values.name)) {
            errors.name = 'Must include a name.';
        }
        if (isEmpty(values.companyNames)) {
            errors.companyNames = 'Must include at least one company login name.';
        }
        if (!isEmpty(values.permalink) && !values.permalink.match('^[a-zA-Z0-9-]+$')) {
            errors.permalink = 'Only use letters, numbers, and dashes.';
        }
        return errors;
    }
};
export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanySettingsFormValidation)
)(CompanySettingsForm);
